import {
  FontIcon,
  IBasePicker,
  IBasePickerSuggestionsProps,
  IInputProps,
  IPickerItemProps,
  ISuggestionItemProps,
  ITag,
  TagPicker,
  ValidationState,
} from '@fluentui/react';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ISingleTag, SingleTag } from 'components/Tags/SingleTag';
import { globalSuggestionsMaxRecords } from 'globalConstants';
import { TagInvalidChars } from 'models/tag';
import React, { createRef, forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Sentinel key for the synthetic "create new" suggestion row.
const CREATE_KEY = '__create_new__';

interface ISingleTagPickerProps {
  tags: ISingleTag[];
  selectedTags?: ISingleTag[];
  itemLimit?: number;
  disabled?: boolean;
  onRemove?: (item: ISingleTag) => void;
  onAdd?: (item: ISingleTag, isNew: boolean) => void;
  onCreate?: (item: ISingleTag) => void;
  isLoading?: boolean;
  colorForNewItems?: string;
  preventTabKey?: boolean;
  defaultValue?: string | undefined;
  placeholder?: string;
  // When set, typing a name with no exact match shows a "{createNewLabel}: '<typed>'" suggestion so it
  // is clear a new unique item will be created (the picker already allows creating via Enter/blur).
  createNewLabel?: string;
}

const SingleTagPicker = forwardRef((props: ISingleTagPickerProps, ref) => {
  const { t } = useTranslation(['translation', 'tag']);
  const [selectedTags, setSelectedTags] = useState<ISingleTag[] | undefined>(props.selectedTags);
  const tagPickerRef = createRef<IBasePicker<ITag>>();

  useEffect(() => {
    setSelectedTags(props.selectedTags);
  }, [props.selectedTags]);

  const getITag = (tag: ISingleTag): ITag => {
    return {
      name: tag.tagName,
      key: tag.tagId.toString(),
    };
  };

  const getITags = (tags: ISingleTag[] | undefined): ITag[] | undefined => {
    return tags?.map((t) => getITag(t));
  };

  const onResolveSuggestions = async (filter: string, selectedItems: ITag[] | undefined): Promise<ITag[]> => {
    if (filter && props.tags) {
      const filterLower = filter.toLowerCase();

      const matches = props.tags
        .filter(
          (tag: ISingleTag) =>
            !selectedTags?.some((t) => t.tagId === tag.tagId) && tag.tagName.toLowerCase().includes(filterLower),
        )
        .sort((a, b) => {
          return a.tagName.localeCompare(b.tagName);
        })
        .map((tag: ISingleTag): ITag => {
          return getITag(tag);
        });

      // Offer an explicit "create new" row when the typed name does not already exist, so it is clear
      // a new unique item will be created instead of just showing "No items found".
      if (props.createNewLabel) {
        const exists = props.tags.some((tag) => tag.tagName.toLowerCase() === filterLower);
        if (!exists) {
          matches.push({ key: CREATE_KEY, name: filter.trim() });
        }
      }

      return matches;
    }

    return [];
  };

  const suggestionProps: IBasePickerSuggestionsProps = {
    showRemoveButtons: false,
    resultsMaximumNumber: globalSuggestionsMaxRecords,
    noResultsFoundText: t('translation:General.Suggestions.NoData'),
  };

  const onRenderSuggestionsItem = (pickerProps: ITag, itemProps: ISuggestionItemProps<ITag>): JSX.Element => {
    const tagItem = pickerProps;

    if (tagItem.key === CREATE_KEY) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px' }}>
          <FontIcon iconName="Add" aria-hidden={true} />
          <span>
            {props.createNewLabel}: <span style={{ opacity: 0.7 }}>&quot;{tagItem.name}&quot;</span>
          </span>
        </span>
      );
    }

    if (!props.tags) return <span />;

    const tag = props.tags.find((t) => t.tagId.toString() === tagItem.key);
    if (!tag) return <span />;

    return <SingleTag key={tag.tagId} tag={tag} />;
  };

  const removeSelectedItem = (tag: ISingleTag) => {
    const items = selectedTags?.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(items);
    if (props.onRemove) {
      props.onRemove(tag);
    }
  };

  const onRenderItem = (pickerProps: IPickerItemProps<ITag>): JSX.Element => {
    const tagItem = pickerProps.item;
    if (!selectedTags) return <span />;

    const tag = selectedTags.find((t) => t.tagId.toString() === tagItem.key);
    if (!tag) return <span />;

    return <SingleTag key={tag.tagId} tag={tag} onRemove={removeSelectedItem} />;
  };

  const onSelectItem = (item?: ITag | undefined) => {
    if (item && item.name) {
      if (!item.key || item.key === CREATE_KEY) {
        item.key = item.name;
        const newTag: ISingleTag = {
          tagId: item.key,
          tagName: item.name,
          tagColor: props.colorForNewItems || '#aaaaaa',
        };
        const newSelectedTags = selectedTags?.slice(0) || [];
        newSelectedTags.push(newTag);
        setSelectedTags(newSelectedTags);
        if (props.onAdd) {
          props.onAdd(newTag, true);
        }
      } else {
        const tag = props.tags?.find((t) => t.tagId.toString() === item.key);
        if (tag) {
          const newSelectedTags = selectedTags?.slice(0) || [];
          newSelectedTags.push(tag);
          setSelectedTags(newSelectedTags);
          if (props.onAdd) {
            props.onAdd(tag, false);
          }
        }
      }

      return item;
    }

    return null;
  };

  const onKeyPress = (e: React.KeyboardEvent) => {
    if (props.preventTabKey && e.key === 'Tab') {
      e.preventDefault();
    }
  };

  const onEmptyResolveSuggestions = (selectedItems?: ITag[] | undefined): ITag[] => {
    return props.tags
      .filter((tag: ISingleTag) => !selectedItems?.some((t) => t.key.toString() === tag.tagId.toString()))
      .sort((a, b) => {
        return a.tagName.localeCompare(b.tagName);
      })
      .slice(0, 50)
      .map((tag: ISingleTag): ITag => {
        return getITag(tag);
      });
  };

  const inputProps: IInputProps = {
    defaultVisibleValue: props.defaultValue,
    placeholder: props.placeholder ? props.placeholder : t('tag:Picker.PlaceholderGroup'),
  };

  //
  // Main render
  //
  return (
    <IPStack onKeyDown={onKeyPress}>
      <TagPicker
        ref={ref}
        inputProps={inputProps}
        componentRef={tagPickerRef}
        itemLimit={props.itemLimit}
        onResolveSuggestions={onResolveSuggestions}
        pickerSuggestionsProps={suggestionProps}
        selectedItems={getITags(selectedTags)}
        onRenderSuggestionsItem={onRenderSuggestionsItem}
        onEmptyResolveSuggestions={onEmptyResolveSuggestions}
        disabled={props.disabled}
        onRenderItem={onRenderItem}
        createGenericItem={(input: string) => {
          //replace invalid characters
          TagInvalidChars.forEach((c) => (input = input.replaceAll(c, '-')));

          return { key: '', name: input.trim() } as ITag;
        }}
        onItemSelected={onSelectItem}
        onValidateInput={(input: string) => {
          return input ? ValidationState.valid : ValidationState.invalid;
        }}
        onBlur={() => tagPickerRef.current?.completeSuggestion(true)}
      />
    </IPStack>
  );
});

export default SingleTagPicker;
